/**
 * #402 — decisão dos atalhos Enter/Esc da chamada entrante.
 *
 * O handler antigo do `incoming-call-modal.tsx` não inspecionava nada: qualquer
 * Enter na janela atendia a ligação. Como o composer do chat é um `<textarea>`
 * e a chamada chega justamente enquanto o atendente escreve, apertar Enter para
 * ENVIAR a mensagem atendia a ligação sem querer.
 *
 * A decisão vive aqui, fora do componente, pelo mesmo motivo do
 * `active-call-reducer.ts` deste diretório: o modal não tem teste, e lógica
 * dentro de `useEffect` é lógica que ninguém consegue cobrir.
 */

/** Ação que o atalho representa, ou `null` quando o atalho não deve agir. */
export type IncomingCallShortcut = 'accept' | 'reject' | null;

/**
 * `true` quando o alvo do evento é um campo de digitação — onde Enter e Esc
 * pertencem ao campo, não à ligação.
 *
 * `target` é `unknown` de propósito: o chamador passa `e.target`, que o DOM
 * tipa como `EventTarget | null` (sem `tagName`), e o teste passa objetos
 * literais. O que interessa é só o formato mínimo
 * `{ tagName, isContentEditable }`.
 */
export function isTextEntryTarget(target: unknown): boolean {
  if (!target || typeof target !== 'object') return false;
  const el = target as { tagName?: unknown; isContentEditable?: unknown };
  if (el.isContentEditable === true) return true;
  const tag = typeof el.tagName === 'string' ? el.tagName.toUpperCase() : '';
  return tag === 'INPUT' || tag === 'TEXTAREA';
}

/**
 * Traduz uma tecla + o alvo do evento na ação da chamada entrante.
 * Devolve `null` sempre que o atalho não deve agir — inclusive para teclas
 * que não são Enter nem Escape.
 */
export function shouldHandleCallShortcut(
  key: string,
  target: unknown,
): IncomingCallShortcut {
  if (isTextEntryTarget(target)) return null;
  if (key === 'Enter') return 'accept';
  if (key === 'Escape') return 'reject';
  return null;
}
